import React, { useState, useEffect, useMemo } from "react";
import BackMethods from "../../back_component/BackMethods";
import Establishment from "../../models/Establishment";
import img from "../../1.webp";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function EstablishmentsPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { backEnd_request, getUserConstraints } = BackMethods();

    const {
        userGovernorate,
        isSuperAdmin,
    } = getUserConstraints();

    // --- 1. قراءة القيم من الرابط (URL) ---
    const urlPage = parseInt(searchParams.get("page")) || 1;
    const urlSize = parseInt(searchParams.get("size")) || 20;
    const urlQ = searchParams.get("q") || "";
    const urlGov = searchParams.get("gov") || userGovernorate || "";
    const urlCity = searchParams.get("city") || "";
    const urlSector = searchParams.get("sector") || "";
    const urlSortBy = searchParams.get("sortBy") || "name";
    const urlSortDir = searchParams.get("sortDir") || "asc";
    // القيم الجديدة من الـ URL
    const urlFacilityType = searchParams.get("facilityType") || "";
    const urlActivityType = searchParams.get("activityType") || "";

    // --- 2. States البيانات ---
    const [establishments, setEstablishments] = useState([]);
    const [locationsData, setLocationsData] = useState([]);
    const [facilityTypesList, setFacilityTypesList] = useState([]); // قائمة أنواع الفعاليات
    const [activitiesList, setActivitiesList] = useState([]);     // قائمة الأنشطة
    const [isLoading, setIsLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(1);
    const [totalEstablishments, setTotalEstablishments] = useState(0);
    const [viewMode, setViewMode] = useState('cards');

    // --- 3. States الفلاتر ---
    const [currentPage, setCurrentPage] = useState(urlPage);
    const [pageSize, setPageSize] = useState(urlSize);
    const [searchQuery, setSearchQuery] = useState(urlQ);
    const [selectedGov, setSelectedGov] = useState(urlGov);
    const [selectedCity, setSelectedCity] = useState(urlCity);
    const [selectedSector, setSelectedSector] = useState(urlSector);
    const [selectedFacilityType, setSelectedFacilityType] = useState(urlFacilityType);
    const [selectedActivityType, setSelectedActivityType] = useState(urlActivityType);
    const [tempSortBy, setTempSortBy] = useState(urlSortBy);
    const [tempSortDir, setTempSortDir] = useState(urlSortDir);

    // دالة جلب البيانات الأساسية
    const fetchEstablishments = async () => {
        setIsLoading(true);
        try {
            const res = await backEnd_request.get("/Establishments", {
                params: {
                    page: urlPage,
                    size: urlSize,
                    q: urlQ,
                    governorate: urlGov,
                    city: urlCity,
                    district: urlSector,
                    facilityType: urlFacilityType, // الفلتر الجديد
                    activityType: urlActivityType, // الفلتر الجديد
                    sortBy: urlSortBy,
                    sortDir: urlSortDir
                },
            });
            // console.log(res.data.data);
            if (res.data?.data) {
                const estObjects = res.data.data.map((e) => new Establishment(e));
                setEstablishments(estObjects);
                setTotalEstablishments(parseInt(res.data.total) || 0);
                setTotalPages(Math.ceil((parseInt(res.data.total) || 0) / urlSize));
            }
        } catch (error) {
            console.error("Fetch Error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // جلب بيانات المواقع والفعاليات والأنشطة عند التحميل الأول
    useEffect(() => {

        const params = {
            size: 1000,
        };
        const fetchInitialData = async () => {
            try {
                const [locRes, facRes, actRes] = await Promise.all([
                    backEnd_request.get("/Locations?includeInactive=false", { params }),
                    backEnd_request.get("/FacilityTypes"),
                    backEnd_request.get("/Activities?page=1&size=200")
                ]);
                // console.log(locRes.data.data)
                setLocationsData(locRes.data.data || []);
                setFacilityTypesList(facRes.data || []);
                setActivitiesList(actRes.data?.data || []);
            } catch (error) {
                console.error("Error fetching initial data:", error);
            }
        };
        fetchInitialData();
    }, []);

    // تحديث الـ States عند تغيير الرابط
    useEffect(() => {
        setCurrentPage(urlPage);
        setPageSize(urlSize);
        setSearchQuery(urlQ);
        setSelectedGov(urlGov);
        setSelectedCity(urlCity);
        setSelectedSector(urlSector);
        setSelectedFacilityType(urlFacilityType);
        setSelectedActivityType(urlActivityType);
        setTempSortBy(urlSortBy);
        setTempSortDir(urlSortDir);

        fetchEstablishments();
    }, [searchParams]);

    // مذكرات المواقع (Memoized)
    const availableGovernorates = useMemo(() => [...new Set(locationsData.map(l => l.governorate))], [locationsData]);
    const availableCities = useMemo(() => {
        if (!selectedGov) return [];
        return [...new Set(locationsData.filter(l => l.governorate === selectedGov).map(l => l.city))];
    }, [locationsData, selectedGov]);
    const availableSectors = useMemo(() => {
        if (!selectedCity) return [];
        return locationsData.filter(l => l.governorate === selectedGov && l.city === selectedCity).map(l => l.sector);
    }, [locationsData, selectedGov, selectedCity]);

    // تطبيق الفلاتر
    const applyFilters = (overrides = {}) => {
        const params = {
            page: overrides.page || 1,
            size: overrides.size || pageSize,
            q: searchQuery,
            gov: selectedGov,
            city: selectedCity,
            sector: selectedSector,
            facilityType: selectedFacilityType,
            activityType: selectedActivityType,
            sortBy: tempSortBy,
            sortDir: tempSortDir,
            ...overrides
        };
        Object.keys(params).forEach(key => !params[key] && delete params[key]);
        setSearchParams(params);
    };

    const handleSearchClick = () => applyFilters({ page: 1 });

    const handlePageChange = (p) => {
        if (p < 1 || p > totalPages) return;
        applyFilters({ page: p });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const resetFilters = () => {
        const defaultGov = isSuperAdmin ? "" : userGovernorate;
        setSelectedGov(defaultGov);
        setSelectedCity("");
        setSelectedSector("");
        setSelectedFacilityType("");
        setSelectedActivityType("");
        setSearchQuery("");
        setTempSortBy("name");
        setTempSortDir("asc");
        setSearchParams({ page: 1, size: 20, gov: defaultGov, sortBy: 'name', sortDir: 'asc' });
    };

    return (
        <div className="container py-4 min-vh-100" style={{ backgroundColor: "#f4f7f6", direction: "rtl" }}>

            {/* Header & Filters Card */}
            <div className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
                <div className="card-body p-4 bg-white">
                    <div className="row g-3">
                        {/* عمود العنوان والإحصائيات */}
                        <div className="col-12 col-lg-2 text-center text-lg-end border-start">
                            <h2 className="fw-bold text-dark mb-1 fs-4">إدارة المنشآت</h2>
                            <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2 mb-2">
                                الإجمالي: {totalEstablishments}
                            </span>
                            <button className="btn btn-link btn-sm text-decoration-none text-muted d-block w-100" onClick={resetFilters}>
                                <i className="bi bi-arrow-counterclockwise"></i> إعادة ضبط
                            </button>
                        </div>

                        {/* قسم الفلاتر (مقسم لصفين لسهولة العرض) */}
                        <div className="col-12 col-lg-10">
                            <div className="row g-3">

                                {/* السطر الأول: الموقع الجغرافي (3 فلاتر + فراغ متوازن) */}
                                <div className="col-md-4">
                                    <label className="small text-muted mb-1 fw-bold">المحافظة</label>
                                    <select className="form-select border-0 bg-light shadow-sm" value={selectedGov} disabled={!isSuperAdmin}
                                        onChange={(e) => { setSelectedGov(e.target.value); setSelectedCity(""); setSelectedSector(""); }}>
                                        <option value="">كل المحافظات</option>
                                        {availableGovernorates.map(g => <option key={g} value={g}>{g}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <label className="small text-muted mb-1 fw-bold">المدينة</label>
                                    <select className="form-select border-0 bg-light shadow-sm" value={selectedCity} disabled={!selectedGov}
                                        onChange={(e) => { setSelectedCity(e.target.value); setSelectedSector(""); }}>
                                        <option value="">كل المدن</option>
                                        {availableCities.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <label className="small text-muted mb-1 fw-bold">القطاع / المنطقة</label>
                                    <select className="form-select border-0 bg-light shadow-sm" value={selectedSector} disabled={!selectedCity}
                                        onChange={(e) => setSelectedSector(e.target.value)}>
                                        <option value="">كل القطاعات</option>
                                        {availableSectors.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>

                                {/* السطر الثاني: نوع المنشأة، النشاط، الترتيب، والبحث */}
                                <div className="col-md-2">
                                    <label className="small text-muted mb-1 fw-bold">نوع الفعالية</label>
                                    <input list="facilityTypesOptions" className="form-control border-0 bg-light shadow-sm"
                                        value={selectedFacilityType} placeholder="بحث..."
                                        onChange={(e) => setSelectedFacilityType(e.target.value)} />
                                    <datalist id="facilityTypesOptions">
                                        {facilityTypesList.map(f => <option key={f.facilityTypeId} value={f.name} />)}
                                    </datalist>
                                </div>

                                <div className="col-md-2">
                                    <label className="small text-muted mb-1 fw-bold">النشاط</label>
                                    <input list="activitiesOptions" className="form-control border-0 bg-light shadow-sm"
                                        value={selectedActivityType} placeholder="بحث..."
                                        onChange={(e) => setSelectedActivityType(e.target.value)} />
                                    <datalist id="activitiesOptions">
                                        {activitiesList.map(a => <option key={a.activityId} value={a.activityType} />)}
                                    </datalist>
                                </div>

                                <div className="col-md-3">
                                    <label className="small text-muted mb-1 fw-bold">فرز حسب</label>
                                    <div className="input-group shadow-sm">
                                        <select className="form-select border-0 bg-light small" value={tempSortBy} onChange={(e) => setTempSortBy(e.target.value)}>
                                            <option value="name">الاسم</option>
                                            <option value="violations">الضبوط</option>
                                            <option value="visits">الزيارات</option>
                                            <option value="complaints">الشكاوي</option>
                                        </select>
                                        <select className="form-select border-0 bg-light border-start small" style={{ maxWidth: '70px' }} value={tempSortDir} onChange={(e) => setTempSortDir(e.target.value)}>
                                            <option value="asc">↑</option>
                                            <option value="desc">↓</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <label className="small text-muted mb-1 fw-bold">اسم المنشأة</label>
                                    <div className="input-group shadow-sm rounded-3 overflow-hidden">
                                        <span className="input-group-text bg-light border-0"><i className="bi bi-search"></i></span>
                                        <input type="text" className="form-control border-0 bg-light ps-0" placeholder="اسم او رقم المنشأة..."
                                            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleSearchClick()} />
                                    </div>
                                </div>

                                <div className="col-md-2 d-flex align-items-end">
                                    <button className="btn btn-primary w-100 shadow-sm fw-bold border-0" onClick={handleSearchClick} style={{ height: '38px', borderRadius: '8px' }}>
                                        <i className="bi bi-funnel-fill me-1"></i> تصفية
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* View Mode Toggle */}
            <div className="d-flex justify-content-end mb-3 align-items-center gap-3">
                <div className="bg-white p-1 rounded-3 shadow-sm border d-inline-flex">
                    <button className={`btn btn-sm rounded-2 px-3 ${viewMode === 'cards' ? 'btn-primary' : 'btn-light text-muted'}`} onClick={() => setViewMode('cards')}>
                        <i className="bi bi-grid-fill"></i> كروت
                    </button>
                    <button className={`btn btn-sm rounded-2 px-3 ${viewMode === 'table' ? 'btn-primary' : 'btn-light text-muted'}`} onClick={() => setViewMode('table')}>
                        <i className="bi bi-list-task"></i> جدول
                    </button>
                </div>
            </div>

            {/* Results Section */}
            {isLoading ? (
                <div className="text-center py-5">
                    <div className="spinner-grow text-primary"></div>
                    <p className="mt-3 text-muted">جاري تحميل المنشآت...</p>
                </div>
            ) : establishments.length === 0 ? (
                <div className="text-center py-5 bg-white rounded-4 shadow-sm border">
                    <i className="bi bi-building-exclamation text-muted display-1"></i>
                    <h4 className="mt-3 text-dark">لا توجد منشآت مطابقة</h4>
                    <p className="text-muted">حاول تغيير خيارات البحث أو التأكد من الاسم المكتوب.</p>
                </div>
            ) : (
                <div className="animate-fade-in">
                    {viewMode === 'cards' && (
                        <div className="row g-4">
                            {establishments.map((est) => (
                                <div key={est.establishmentId} className="col-12 col-sm-6 col-xl-3">
                                    <div className="card border-0 h-100 shadow-hover transition-all rounded-4 overflow-hidden"
                                        onClick={() => navigate(`/admin/establishments/${est.fieldUID}`)}
                                        style={{ cursor: 'pointer' }}>
                                        <div className="position-relative">
                                            <img src={est.externalPhoto?.photoUrl || img} className="card-img-top" style={{ height: '180px', objectFit: 'cover' }} alt="..." />
                                            <div className="position-absolute top-0 end-0 m-2">
                                                <span className="badge bg-light  border bg-opacity-0 backdrop-blur px-3 py-2 rounded-pill small">
                                                    {est.facilityType || "منشأة" }
                                                </span>
                                            </div>
                                        </div>
                                        <div className="card-body p-3">
                                            <h6 className="fw-bold text-dark text-truncate mb-2" title={est.name}>{est.name}</h6>
                                            <div className="small text-muted mb-3">
                                                <i className="bi bi-geo-alt-fill text-danger me-1"></i>
                                                {est.city} {est.district ? `(${est.district})` : ''}
                                            </div>
                                            <div className="d-flex justify-content-between gap-1 mt-auto">
                                                <div className="text-center flex-fill p-2 bg-light rounded-3">
                                                    <div className="extra-small text-muted">الشكاوي</div>
                                                    <div className="fw-bold text-info">{est.publicComplaintsCount}</div>
                                                </div>
                                                <div className={`text-center flex-fill p-2 rounded-3 ${est.violationReportsCount > 0 ? 'bg-danger-subtle text-danger' : 'bg-light text-muted'}`}>
                                                    <div className="extra-small">ضبوط</div>
                                                    <div className="fw-bold">{est.violationReportsCount || 0}</div>
                                                </div>
                                                <div className="text-center flex-fill p-2 bg-light rounded-3">
                                                    <div className="extra-small text-muted">زيارات</div>
                                                    <div className="fw-bold text-primary">{est.inspectionVisitsCount || 0}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {viewMode === 'table' && (
                        <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="bg-light text-muted small">
                                        <tr>
                                            <th className="ps-4">المنشأة</th>
                                            <th>الموقع</th>
                                            <th className="text-center">الشكاوي</th>
                                            <th className="text-center">الضبوط</th>
                                            <th className="text-center">الزيارات</th>

                                        </tr>
                                    </thead>
                                    <tbody>
                                        {establishments.map((est) => (
                                            <tr key={est.establishmentId} style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/establishments/${est.fieldUID}`)}>
                                                <td className="ps-4">
                                                    <div className="fw-bold text-dark">{est.name}</div>
                                                    <div className="text-muted extra-small">{est.facilityType}</div>
                                                </td>
                                                <td>
                                                    <div className="small fw-semibold">{est.governorate}</div>
                                                    <div className="text-muted extra-small">{est.city} - {est.district}</div>
                                                </td>
                                                <td className="text-center">
                                                    {/* <span className="badge bg-light text-primary border me-1">👤 {est.persons}</span>
                                                    <span className="badge bg-light text-info border me-1">⚡ {est.activities}</span>
                                                    <span className="badge bg-light text-danger border me-1">🔨 {est.violationReportsCount}</span>
                                                    <span className="badge bg-light text-primary border">👁️ {est.inspectionVisitsCount || 0}</span> */}
                                                </td>

                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Pagination */}
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-5 gap-3 bg-white p-3 rounded-4 shadow-sm">
                        <div className="text-muted small">
                            عرض الصفحة {currentPage} من أصل {totalPages}
                        </div>
                        <div className="d-flex align-items-center gap-2 border-start ps-3">
                            <label className="extra-small text-muted mb-0">العناصر:</label>
                            <select
                                className="form-select form-select-sm border-0 bg-light shadow-none"
                                style={{ width: 'auto', cursor: 'pointer' }}
                                value={pageSize}
                                onChange={(e) => applyFilters({ size: parseInt(e.target.value), page: 1 })}
                            >
                                <option value={12}>12</option>
                                <option value={20}>20</option>
                                <option value={32}>32</option>
                                <option value={40}>40</option>
                            </select>
                        </div>
                        <nav>
                            <ul className="pagination pagination-sm mb-0 gap-1 flex-wrap justify-content-center">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button className="page-link rounded-circle border-0 shadow-sm p-2" onClick={() => handlePageChange(currentPage - 1)}>
                                        <i className="bi bi-chevron-right px-1"></i>
                                    </button>
                                </li>
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                                    .map((p, idx, arr) => (
                                        <React.Fragment key={p}>
                                            {idx > 0 && arr[idx - 1] !== p - 1 && <span className="mx-1 text-muted">...</span>}
                                            <li className={`page-item ${currentPage === p ? 'active' : ''}`}>
                                                <button className="page-link rounded-circle border-0 shadow-sm fw-bold mx-1" style={{ width: '38px', height: '38px' }} onClick={() => handlePageChange(p)}>{p}</button>
                                            </li>
                                        </React.Fragment>
                                    ))}
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button className="page-link rounded-circle border-0 shadow-sm p-2" onClick={() => handlePageChange(currentPage + 1)}>
                                        <i className="bi bi-chevron-left px-1"></i>
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </div>
            )}
        </div>
    );
}